import { useState } from "react";
import { ChevronDown, Plus, Repeat } from "lucide-react";
import { RecurringFrequency } from "@boo/shared";
import { useAppStore } from "../../store/useAppStore";
import { useRecurringIncomeStore } from "../../store/useRecurringIncomeStore";
import RecurringIncomeRow from "./RecurringIncomeRow";
import RecurringIncomeModal from "./RecurringIncomeModal";

export default function RecurringIncomeCard() {
  const {
    items: allItems,
    loading,
    applying,
    openCreateModal,
  } = useRecurringIncomeStore();
  const viewYear = useAppStore((s) => s.viewYear);
  const viewMonth = useAppStore((s) => s.viewMonth);
  // Mobile only: the list is collapsed to a one-line summary until opened.
  const [open, setOpen] = useState(false);

  // Hide incomes whose end period is before the viewed month
  const items = allItems.filter(
    (item) =>
      item.endYear == null ||
      item.endMonth == null ||
      item.endYear * 12 + item.endMonth >= viewYear * 12 + viewMonth,
  );
  const monthlyTotal = items
    .filter((item) => item.frequency === RecurringFrequency.MONTHLY)
    .reduce((sum, item) => sum + item.amount, 0);

  return (
    <>
      <div className="rounded-[14px] px-5 py-5 sm:px-6 bg-card border-[0.5px] border-border shadow-card">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0" onClick={() => setOpen((o) => !o)}>
            <h2 className="flex items-center gap-2 text-[15px] font-semibold text-foreground">
              <Repeat className="w-4 h-4 text-muted-foreground" />
              Recurring income
            </h2>
            {!loading && items.length > 0 && (
              <p className="sm:hidden mt-0.5 pl-6 text-[12px] text-muted-foreground [font-variant-numeric:tabular-nums]">
                {items.length} {items.length === 1 ? "source" : "sources"} · ฿
                {Math.round(monthlyTotal).toLocaleString("en-US")} / mo
              </p>
            )}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={openCreateModal}
              className="flex items-center gap-1.5 text-[12px] font-medium text-muted-foreground hover:text-foreground px-2.5 py-1.5 rounded-[7px] hover:bg-[var(--surface-muted)] transition-colors cursor-pointer"
            >
              <Plus className="w-3.5 h-3.5" />
              Add
            </button>
            <button
              onClick={() => setOpen((o) => !o)}
              aria-expanded={open}
              aria-label={
                open ? "Hide recurring income" : "Show recurring income"
              }
              className="sm:hidden p-2 -mr-2 rounded-[7px] text-muted-foreground hover:text-foreground hover:bg-[var(--surface-muted)] transition-colors cursor-pointer"
            >
              <ChevronDown
                className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`}
              />
            </button>
          </div>
        </div>

        <div className={`mt-4 ${open ? "" : "hidden sm:block"}`}>
          {applying && (
            <div className="mb-3 text-[11px] text-muted-foreground animate-pulse">
              Applying recurring income…
            </div>
          )}

          {loading ? (
            <div className="flex flex-col gap-2">
              {[1, 2].map((i) => (
                <div
                  key={i}
                  className="h-[52px] rounded-[10px] bg-[var(--surface-muted)] animate-pulse"
                />
              ))}
            </div>
          ) : items.length === 0 ? (
            <p className="text-[12px] text-muted-foreground py-2">
              No recurring income set up yet.
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {items.map((item) => (
                <RecurringIncomeRow key={item.id} item={item} />
              ))}
            </div>
          )}
        </div>
      </div>

      <RecurringIncomeModal />
    </>
  );
}
